import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./Trustpilot.module.scss";

export interface TrustpilotWidgetProps {
    dataTemplateId: string;
    dataBusinessUnitId: string;
    theme?: string;
    stars?: string;
    locale?: string;
}

export const SSH_Trustpilot: React.FC<TrustpilotWidgetProps> = (props) => {
    const { currentCulture } = useAppData();
    const trustpilotRef = useRef<HTMLDivElement>(null);
    const { dataTemplateId, dataBusinessUnitId, theme, stars } = props;
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        let attempts = 0;
        const maxAttempts = 15; // Try for up to 7.5 seconds (15 * 500ms)

        const loadTrustpilot = () => {
            // @ts-ignore
            const trustpilot = window.Trustpilot;

            if (trustpilot && trustpilotRef.current) {
                try {
                    trustpilot.loadFromElement(trustpilotRef.current, true);
                    setLoading(false);
                    if (interval) clearInterval(interval);
                } catch (error) {
                    console.warn("[Trustpilot] Failed to load widget:", error);
                    setLoading(false);
                    if (interval) clearInterval(interval);
                }
            } else {
                attempts++;
                if (attempts >= maxAttempts) {
                    console.info(
                        "[Trustpilot] Script blocked or unavailable (likely tracking protection)"
                    );
                    setLoading(false);
                    if (interval) clearInterval(interval);
                }
            }
        };

        // Check if script element exists and loaded successfully
        const checkScriptLoaded = () => {
            const scripts = document.querySelectorAll('script[src*="trustpilot"]');
            if (scripts.length > 0) {
                // Script tag exists, wait for it to execute
                loadTrustpilot();

                // @ts-ignore
                if (!window.Trustpilot && attempts < maxAttempts) {
                    interval = setInterval(loadTrustpilot, 500);
                }
            } else {
                // No script found, might be blocked
                console.info("[Trustpilot] Script element not found - may be blocked by browser");
                setLoading(false);
            }
        };

        const timeout = setTimeout(checkScriptLoaded, 300);

        return () => {
            clearTimeout(timeout);
            if (interval) clearInterval(interval);
        };
    }, [dataTemplateId, dataBusinessUnitId, theme, stars, currentCulture]);

    return (
        <>
            <Helmet>
                <script
                    src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
                    async
                    type="text/javascript"
                />
            </Helmet>

            {loading && (
                <div className={cn(S.trustpilot, S[theme.toLowerCase()], S.loader)}>
                    <DA_Spinner className={S.spinner} />
                </div>
            )}

            <div
                ref={trustpilotRef}
                className={cn(S.trustpilot, S[theme.toLowerCase()])}
                data-template-id={dataTemplateId}
                data-businessunit-id={dataBusinessUnitId} // Fetch reviews and Trustpilot profile link
                data-stars={stars}
                data-style-width="100%"
                data-locale={currentCulture ?? "da-dk"}
                data-theme={theme ?? "light"}
            />
        </>
    );
};
